import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { ShieldCheck, Download, FlaskConical, Save, FileText, FileCode2 } from "lucide-react";
import { API_URL } from "../../lib/utils";

const field = "bg-white/5 border-white/15 text-white";

export const FiscalTab = () => {
  const [info, setInfo] = useState(null);
  const [form, setForm] = useState({ validation_number: "", company_name: "", company_nif: "", company_address: "" });
  const [saving, setSaving] = useState(false);
  const [selftest, setSelftest] = useState(null);

  const load = async () => {
    const r = await axios.get(`${API_URL}/backoffice/fiscal`, { withCredentials: true });
    setInfo(r.data);
    setForm({ validation_number: r.data.validation_number, company_name: r.data.company_name, company_nif: r.data.company_nif, company_address: r.data.company_address });
  };
  useEffect(() => { load().catch(() => toast.error("Erro ao carregar dados fiscais")); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/backoffice/fiscal`, form, { withCredentials: true });
      toast.success("Dados do produtor guardados — passam a constar em todos os documentos e no SAF-T");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao guardar");
    } finally {
      setSaving(false);
    }
  };

  const runSelftest = async () => {
    try {
      const r = await axios.post(`${API_URL}/backoffice/fiscal/selftest`, {}, { withCredentials: true });
      setSelftest(r.data);
      (r.data.verified ? toast.success : toast.error)(r.data.verified ? "Par de chaves RSA verificado" : "Falha na verificação da assinatura");
    } catch {
      toast.error("Erro no auto-teste");
    }
  };

  const downloadKey = async () => {
    const r = await axios.get(`${API_URL}/backoffice/fiscal/public-key`, { withCredentials: true, responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = `lumora_public_key_v${info?.key_version || 1}.pem`; a.click(); URL.revokeObjectURL(url);
  };

  const [dossierBusy, setDossierBusy] = useState("");
  const downloadFile = async (path, filename, kind) => {
    setDossierBusy(kind);
    try {
      const r = await axios.get(`${API_URL}/backoffice/fiscal/${path}`, { withCredentials: true, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click(); URL.revokeObjectURL(url);
      toast.success(kind === "pdf" ? "Dossier de validação gerado" : `SAF-T de exemplo gerado${r.headers["x-saft-xsd-valid"] === "1" ? " · XSD válido" : ""}`);
    } catch {
      toast.error("Não foi possível gerar o ficheiro");
    } finally {
      setDossierBusy("");
    }
  };

  const certified = info && info.validation_number !== "0";

  return (
    <div className="space-y-6" data-testid="backoffice-fiscal-tab">
      <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center"><ShieldCheck className="w-6 h-6 text-cyan-400" /></div>
            <div>
              <h2 className="text-xl font-bold font-['Outfit']">Validação AGT do software</h2>
              <p className="text-sm text-white/50">Decreto Presidencial n.º 312/18 — dados do produtor, n.º de validação e chave pública RSA a entregar à AGT</p>
            </div>
          </div>
          <Badge className={certified ? "bg-emerald-500/20 text-emerald-300" : "bg-amber-500/20 text-amber-300"} data-testid="backoffice-fiscal-status">
            {certified ? `Validado · n.º ${info.validation_number}` : "Aguarda n.º de validação"}
          </Badge>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
          <div className="space-y-1.5">
            <Label className="text-white/70">N.º de validação atribuído pela AGT</Label>
            <Input value={form.validation_number} onChange={(e) => setForm({ ...form, validation_number: e.target.value })} placeholder="0 enquanto não certificado" className={`${field} font-mono`} data-testid="backoffice-validation-number-input" />
            <p className="text-xs text-white/40">Impresso em todos os documentos: "XXXX-Processado por programa validado n.º {form.validation_number || "0"}/AGT".</p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-white/70">Nome do produtor (CompanyName / ProductID)</Label>
            <Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className={field} data-testid="backoffice-producer-name-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-white/70">NIF do produtor (TaxRegistrationNumber)</Label>
            <Input value={form.company_nif} onChange={(e) => setForm({ ...form, company_nif: e.target.value })} className={`${field} font-mono`} data-testid="backoffice-producer-nif-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-white/70">Morada do produtor</Label>
            <Input value={form.company_address} onChange={(e) => setForm({ ...form, company_address: e.target.value })} className={field} />
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mt-5">
          <Button onClick={save} disabled={saving} className="bg-cyan-500 hover:bg-cyan-400 text-black font-semibold" data-testid="backoffice-fiscal-save-btn"><Save className="w-4 h-4 mr-1.5" /> {saving ? "A guardar…" : "Guardar"}</Button>
          <Button variant="outline" onClick={downloadKey} className="border-white/15 bg-transparent text-white/80 hover:bg-white/10" data-testid="backoffice-public-key-btn"><Download className="w-4 h-4 mr-1.5" /> Chave pública (PEM) v{info?.key_version || 1}</Button>
          <Button variant="outline" onClick={runSelftest} className="border-white/15 bg-transparent text-white/80 hover:bg-white/10" data-testid="backoffice-selftest-btn"><FlaskConical className="w-4 h-4 mr-1.5" /> Auto-teste da assinatura</Button>
        </div>
      </div>

      <div className="rounded-2xl border border-cyan-500/20 bg-cyan-500/[0.04] p-6" data-testid="backoffice-dossier-card">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-bold font-['Outfit']">Dossier de validação para a AGT</h3>
            <p className="text-sm text-white/50 mt-1 max-w-2xl">PDF pronto a entregar: identificação do produtor, matriz de requisitos do Decreto Presidencial 312/18, chave pública RSA, auto-teste da assinatura e um SAF-T (AO) de exemplo validado contra o XSD oficial. Não substitui a decisão de validação da AGT.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => downloadFile("dossier.pdf", "dossier_validacao_AGT_lumora.pdf", "pdf")} disabled={!!dossierBusy} className="bg-cyan-500 hover:bg-cyan-400 text-black font-semibold" data-testid="backoffice-dossier-pdf-btn"><FileText className="w-4 h-4 mr-1.5" /> {dossierBusy === "pdf" ? "A gerar…" : "Dossier AGT (PDF)"}</Button>
            <Button variant="outline" onClick={() => downloadFile("sample-saft.xml", "SAFT_AO_exemplo_lumora.xml", "xml")} disabled={!!dossierBusy} className="border-white/15 bg-transparent text-white/80 hover:bg-white/10" data-testid="backoffice-sample-saft-btn"><FileCode2 className="w-4 h-4 mr-1.5" /> {dossierBusy === "xml" ? "A gerar…" : "SAF-T de exemplo (XML)"}</Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5"><p className="text-xs text-white/40">Produto</p><p className="font-mono text-sm mt-1">{info?.product_id}</p><p className="font-mono text-sm">v{info?.product_version}</p></div>
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5"><p className="text-xs text-white/40">Documentos assinados (todas as empresas)</p><p className="text-3xl font-bold mt-1" data-testid="backoffice-signed-docs">{info?.issued_documents ?? "…"}</p></div>
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5"><p className="text-xs text-white/40">Algoritmo</p><p className="text-sm mt-1">RSA 1024 · SHA-1 · PKCS#1 v1.5</p><p className="text-xs text-white/40">texto: Data;Emissão;N.º;Total;HashAnterior</p></div>
      </div>

      {selftest && (
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 space-y-2" data-testid="backoffice-selftest-result">
          <p className="text-sm font-semibold">{selftest.verified ? "✓ Assinatura verificada com a chave pública" : "✗ Verificação falhou"}</p>
          <p className="text-xs text-white/50 font-mono break-all">Texto: {selftest.text}</p>
          <p className="text-xs text-white/50 font-mono break-all">Assinatura: {selftest.signature}</p>
          <p className="text-xs text-white/70 font-mono">Caracteres impressos: {selftest.hash_print}</p>
        </div>
      )}

      {info?.public_key_pem && <pre className="rounded-2xl border border-white/10 bg-black/40 p-4 text-[11px] text-white/60 overflow-auto" data-testid="backoffice-public-key-pem">{info.public_key_pem}</pre>}
    </div>
  );
};
